import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GARMENT_CATEGORIES,
  paiseToRupees,
  parseRupees,
  type GarmentCategory,
  type MeasurementSketchRef,
  type MeasurementTemplate,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useCreateTemplate, useUpdateTemplate } from '../../lib/measurements/use-templates.js';
import { SketchField } from '../measurements/sketch-field.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Naming a garment the shop makes, and saying what it charges for one.
 *
 * TWO FIELDS AND A CATEGORY, deliberately. The measurement FIELDS a garment needs — chest,
 * shoulder, sleeve — are not here: they are a longer job with their own reordering and their own
 * "this field is in use" rules, and they live on the measurements screen where that work belongs.
 * Somebody adding "Bridal cape, ₹15,000" at the counter should not have to walk past twelve
 * measurement rows to do it.
 *
 * THE PRICE MAY BE LEFT BLANK, and the hint says so. "We do bridal lehengas, price on the piece"
 * is a real answer, and a shop forced to invent a figure would put a ₹0 on the board that quietly
 * understates a whole pipeline column. A garment with no price simply reaches a deal with its
 * price box empty and the cursor in it.
 *
 * THE OUTLINE is the one drawing here: the front/back shape every measurement sketch of this
 * garment opens on, locked, for the tailor to draw over. Offered only once the garment exists,
 * because a drawing is made FOR a garment type and a new one has no key until it is saved.
 *
 * THE KEY IS NOT EDITABLE, and is not shown. It is derived from the label when the garment is
 * created and is what every deal, quotation and order stores; renaming the garment moves the
 * label everywhere at once and leaves the key alone, which is exactly what a rename should do.
 *
 * The price is typed in RUPEES and stored in paise, through `parseRupees`, for the reason every
 * money field in this codebase does: `800.10` in binary floating point is not 80010 paise unless
 * somebody does the rounding on purpose.
 */

export interface GarmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing an existing garment; absent when adding one. */
  garment?: MeasurementTemplate | null;
  onSaved?: () => void;
}

export function GarmentFormDialog({
  open,
  onOpenChange,
  garment = null,
  onSaved,
}: GarmentFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const create = useCreateTemplate();
  // Keyed on the garment being edited. Empty while adding, when it is never called.
  const update = useUpdateTemplate(garment?.garmentTypeKey ?? '');

  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<GarmentCategory>('unisex');
  const [price, setPrice] = useState('');
  const [outline, setOutline] = useState<MeasurementSketchRef | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(garment?.garmentTypeLabel ?? '');
    setCategory(garment?.category ?? 'unisex');
    setPrice(garment?.unitPricePaise == null ? '' : String(paiseToRupees(garment.unitPricePaise)));
    setOutline(garment?.outline ?? null);
    setFormError(null);
  }, [open, garment]);

  const busy = create.isPending || update.isPending;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFormError(null);

      const trimmed = label.trim();
      if (!trimmed) {
        setFormError(t('garments.nameRequired'));
        return;
      }

      // Blank is a real answer — "priced on the piece" — so an empty box clears the figure rather
      // than failing validation. Only something typed and unreadable is an error.
      let unitPricePaise: number | null = null;
      if (price.trim()) {
        try {
          unitPricePaise = parseRupees(price.trim());
        } catch {
          setFormError(t('garments.priceInvalid'));
          return;
        }
      }

      try {
        if (garment) {
          await update.mutateAsync({
            garmentTypeLabel: trimmed,
            category,
            unitPricePaise,
            ...(outline?.sketchId !== garment.outline?.sketchId
              ? { outlineSketchId: outline?.sketchId ?? null }
              : {}),
          });
        } else {
          await create.mutateAsync({ garmentTypeLabel: trimmed, category, unitPricePaise });
        }
        toast({ title: garment ? t('garments.saved') : t('garments.created'), variant: 'success' });
        onSaved?.();
        onOpenChange(false);
      } catch (error) {
        setFormError(isApiRequestError(error) ? error.message : t('error.unexpected'));
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="atelier-dialog">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{garment ? t('garments.editTitle') : t('garments.addTitle')}</DialogTitle>
            <DialogDescription>{t('garments.formDescription')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-name`}>{t('garments.nameLabel')}</Label>
              <Input
                id={`${fieldId}-name`}
                value={label}
                autoFocus
                maxLength={60}
                onChange={(event) => setLabel(event.target.value)}
                placeholder={t('garments.namePlaceholder')}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-price`}>{t('garments.priceLabel')}</Label>
                <Input
                  id={`${fieldId}-price`}
                  inputMode="decimal"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  placeholder={t('garments.pricePlaceholder')}
                />
                <p className="text-xs text-muted-foreground">{t('garments.priceHint')}</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-category`}>{t('garments.categoryLabel')}</Label>
                <select
                  id={`${fieldId}-category`}
                  value={category}
                  onChange={(event) => setCategory(event.target.value as GarmentCategory)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  {GARMENT_CATEGORIES.map((one) => (
                    <option key={one} value={one}>
                      {t(`garments.category.${one}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {garment ? (
              <SketchField
                garmentTypeKey={garment.garmentTypeKey}
                name={t('measurementSketch.outlineName', {
                  garment: label.trim() || garment.garmentTypeLabel,
                })}
                value={outline}
                onChange={setOutline}
                heading={t('measurementSketch.outlineHeading')}
                hint={t('measurementSketch.outlineHint')}
                drawLabel={t('measurementSketch.outlineDraw')}
                redrawLabel={t('measurementSketch.outlineRedraw')}
                removeLabel={t('measurementSketch.outlineRemove')}
                disabled={busy}
              />
            ) : null}

            {formError ? (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {garment ? t('common.save') : t('garments.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default GarmentFormDialog;
